import { useMemo } from 'react';

import { useAxios } from '@/hooks/useAxios';
import { AllIntegrations } from '@/hooks/useIntegration';
import { useAuth } from '@/state/auth';
import { Account, IntegrationMeta } from '@/types';
import { useQueries } from '@/utils/api';

/**
 * Returns the list of ticketing provider member names derived from IntegrationMeta.
 * This is the single source of truth — no hardcoded provider arrays elsewhere.
 */
export const TICKETING_PROVIDERS = Object.values(AllIntegrations)
  .filter(i => i.ticketing && !i.hidden)
  .map(i => i.overrideMemberName ?? String(i.id));

/**
 * Returns the set of attribute names used by ticketing integrations.
 * Used to identify ticket attributes on risks.
 */
export const TICKETING_ATTRIBUTE_NAMES = new Set(
  Object.values(AllIntegrations)
    .filter(i => i.ticketing)
    .map(i => i.ticketAttributeName)
    .filter(Boolean) as string[]
);

/**
 * Returns the IntegrationMeta for a ticketing provider member name.
 * Falls back to a generic config if the member isn't recognized.
 */
export function getTicketingIntegration(member: string): IntegrationMeta {
  return (
    AllIntegrations[member as keyof typeof AllIntegrations] ??
    AllIntegrations.jira
  );
}

/**
 * Returns the icon path for a ticketing integration.
 */
export function getTicketingIcon(member: string, theme: string): string {
  const config = getTicketingIntegration(member);
  return `/icons/${theme}/${config.logo}`;
}

/**
 * Hook that fetches accounts for all ticketing providers.
 * Uses useQueries to dynamically handle any number of providers
 * without hardcoding hook slots.
 */
export function useTicketingAccounts() {
  const { isImpersonating, me, friend } = useAuth();
  const axios = useAxios({});
  const user = isImpersonating ? friend : me;

  // Deduplicate provider names (PAT variants share the same member via overrideMemberName)
  const uniqueProviders = useMemo(() => [...new Set(TICKETING_PROVIDERS)], []);

  const queries = useQueries({
    queries: uniqueProviders.map(provider => ({
      queryKey: ['ticketing-accounts', user, friend, provider],
      queryFn: async () => {
        const { data } = await axios.get('/my', {
          params: {
            label: 'account',
            key: `#account#${user}#${provider}`,
          },
        });
        return (data?.accounts ?? []) as Account[];
      },
    })),
  });

  const queriesFingerprint = queries.map(q => q.dataUpdatedAt).join(',');
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const accounts = useMemo(() => {
    return queries.flatMap(q => q.data ?? []);
    // Stabilize: queries array is referentially unstable from useQueries.
    // Depend on loading state and data fingerprint instead.
  }, [queriesFingerprint]);

  const isLoading = queries.some(q => q.status === 'pending');

  return { accounts, isLoading };
}
